import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useWindowSize } from '../../../../libs/hooks'
import { useDeepCompareEffect } from '../../../../hooks/useDeepCompareEffect'
import GenericTable from '../../../common/GenericTable'
import SearchWithFilter from '../../../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../../../common/FiltersModal'
import EmptyPageResults from '../../../common/EmptyPageResults'
import LoadingSpinner from '../../../common/LoadingSpinner'
import Dropdown from '../../../common/Dropdown'
import SimpleButton from '../../../common/SimpleButton'
import ActionsMenu from '../../../common/ActionsMenu'
import { toast } from '../../../common/Toast'
import { DownloadIcon, FilterTableIcon, InformationIcon, MenuKebabVerticalBackgroundIcon, MenuKebabVerticalIcon } from '../../../../icons'
import { downloadFileUTF8, getMonthName, getLastNYearsRangeISO } from '../../../../utils/functions'
import countryData from '../../../../utils/countries.json'
import {
  loadRegisteredUsersChartData,
  loadRegisteredUsersTableData,
  loadRegisteredUsersCountries,
  downloadRegisteredUsersData,
  clearReportsMessages,
} from '../../../../actions/reports'
import { loadRegions } from '../../../../actions/shared'
import UserReportChart from '../UserReportChart'
import ViewUserModal from './ViewUserModal'
import '../../internal-reports.scss'

const orderingFields = [
  {
    field: 'year',
    order: 'asc',
    translationKey: 'year_asc',
  },
  {
    field: 'year',
    order: 'desc',
    translationKey: 'year_desc',
  },
  {
    field: 'accountType',
    order: 'asc',
    translationKey: 'account_type_asc',
  },
  {
    field: 'accountType',
    order: 'desc',
    translationKey: 'account_type_desc',
  },
  {
    field: 'total',
    order: 'asc',
    translationKey: 'total_asc',
  },
  {
    field: 'total',
    order: 'desc',
    translationKey: 'total_desc',
  },
]

const { startISO: defaultStartISO, endISO: defaultEndISO, timeZone } = getLastNYearsRangeISO(5)

const RegisteredUsers = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [registeredUsersTableData, setRegisteredUsersTableData] = useState([])
  const [registeredUsersTableShownData, setRegisteredUsersTableShownData] = useState([])
  const [registeredUsersChartData, setRegisteredUsersChartData] = useState([])
  const [orderBy, setOrderBy] = useState(null)
  // const [searchParam, setSearchParam] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [chartFrequency, setChartFrequency] = useState('year')
  const [chartType, setChartType] = useState('pie')
  const [expandedUser, setExpandedUser] = useState(0)
  const [startDateFilter, setStartDateFilter] = useState(defaultStartISO)
  const [endDateFilter, setEndDateFilter] = useState(defaultEndISO)
  const [selectedFilterCountry, setSelectedFilterCountry] = useState([])
  const [selectedFilterRegion, setSelectedFilterRegion] = useState([])
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showChartFiltersModal, setShowChartFiltersModal] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [tableType, setTableType] = useState('year')
  const [rowId, setRowId] = useState('null')
  const [totalColOrder, setTotalColOrder] = useState(null)
  const PAGE_SIZE = 10
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const hasTable = registeredUsersTableData.length > 0

  const chartTypeKeys = ['line', 'bar', 'stackedBar', 'pie']
  const chartFrequencyKeys = ['month', 'quarter', 'year']

  const chartFilterOptions = [
    {
      name: t('reports.frequency'),
      type: FilterType.singleSelect,
      dataset: chartFrequencyKeys,
      input: chartFrequency,
      output: (selected) => setChartFrequency(selected),
      required: true,
    },
    {
      name: t('reports.chart_type'),
      type: FilterType.singleSelect,
      dataset: chartTypeKeys,
      input: chartType,
      output: (selected) => setChartType(selected),
      required: true,
    },
  ]

  useEffect(() => {
    if (props.registeredUsersCountries.length === 0) {
      props.actions.loadRegisteredUsersCountries()
    }

    if (props.reportsRegions.length === 0) {
      props.actions.loadRegions()
    }
  }, [props.actions])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadTableData(false)
    }
  }, [selectedPage])

  useEffect(() => {
    const chartData = props.registeredUsersChartData.map((record, index) => {
      return {
        index,
        year: record.year,
        month: record.month,
        quarter: record.month ? Math.ceil(record.month / 3) : null,
        accountType: record.accountType,
        total: record.totalRegistered,
      }
    })

    setRegisteredUsersChartData(chartData)
  }, [props.registeredUsersChartData])

  useDeepCompareEffect(() => {
    if (!shouldApplyFilters) return

    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadTableData(true)
        }

        setShouldApplyFilters(true)
      },
      // searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [orderBy, startDateFilter, endDateFilter, selectedFilterRegion, selectedFilterCountry, shouldApplyFilters])

  useEffect(() => {
    const baseRows = getTableData(tableType, props.registeredUsersTableData).map((record, index) => {
      const row = {
        id: index,
        year: record.year,
        month: getMonthName(record?.month),
        accountType: record.accountType,
        countries: [
          ...(record.countries || []).map((country) => ({
            countryName: countryData.find((c) => c.code === country.countryCode.toUpperCase())?.name || 'Unknown',
            count: country.count,
          })),
        ],
        total: record.totalRegistered,
      }
      row.actions = [
        {
          label: 'view_details',
          onActionClick: (e) => {
            e?.stopPropagation?.()
            setShowActionsMenu(null)
            handleOpenViewModal(row) // pass the full row
          },
        },
      ]
      if (tableType === 'year') delete row.month
      return row
    })
    if (totalColOrder) {
      setRegisteredUsersTableData(getOrderedYearTableData(baseRows))
      setRegisteredUsersTableShownData(getOrderedYearTableData(baseRows.slice(0, visibleCount)))
      setTotalColOrder(null)
    } else {
      setRegisteredUsersTableData(baseRows)
      setRegisteredUsersTableShownData(baseRows.slice(0, visibleCount))
    }
  }, [tableType, props.registeredUsersTableData, selectedRecord, visibleCount])

  useDeepCompareEffect(() => {
    if (!shouldApplyFilters) return

    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          handleLoadChartData(true)
        }

        setShouldApplyFilters(true)
      },
      // searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [startDateFilter, endDateFilter, selectedFilterRegion, selectedFilterCountry, shouldApplyFilters])

  const handleOpenViewModal = (row) => {
    setRowId(row.id)
    setSelectedRecord(row)
    setShowViewModal(true)
  }

  const closeModal = () => {
    setShowViewModal(false)
    setSelectedRecord(null)
  }

  const createQueryParams = (type) => {
    let queryParams = {}
    if (startDateFilter) {
      queryParams = { ...queryParams, startDate: startDateFilter }
    }

    if (endDateFilter) {
      queryParams = { ...queryParams, endDate: endDateFilter }
    }

    if (startDateFilter && endDateFilter) {
      queryParams = { ...queryParams, timezone: timeZone }
    }

    if (selectedFilterCountry.length > 0) {
      const countryCodes = props.registeredUsersCountries
        .filter((c) => selectedFilterCountry.includes(c.name))
        .map((c) => c.countryCode)
        .filter(Boolean)

      if (countryCodes.length) {
        queryParams = { ...queryParams, country: countryCodes }
      }
    }

    if (selectedFilterRegion.length > 0) {
      queryParams = { ...queryParams, region: selectedFilterRegion }
    }

    // if (searchParam) {
    //   queryParams = { ...queryParams, query: searchParam }
    // }

    if (orderBy?.field && orderBy?.order) {
      if (orderBy.field === 'total' && tableType === 'year') {
        setTotalColOrder(orderBy.order.toLowerCase())
        queryParams = { ...queryParams }
      } else {
        queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
      }
    }

    if (type === 'chart') {
      // delete queryParams.searchParam
      delete queryParams.orderBy
      delete queryParams.order
      return queryParams
    }

    if (type === 'table') {
      return queryParams
    }
  }

  const handleLoadChartData = (withReset) => {
    let queryParams = createQueryParams('chart')
    queryParams = {
      ...queryParams,
    }
    props.actions.loadRegisteredUsersChartData(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleLoadTableData = (withReset) => {
    let queryParams = createQueryParams('table')
    queryParams = {
      ...queryParams,
    }

    props.actions.loadRegisteredUsersTableData(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleResetFilters = () => {
    setStartDateFilter(null)
    setEndDateFilter(null)
    setSelectedFilterCountry([])
    setSelectedFilterRegion([])
  }

  // const handleSearch = (value) => {
  //   setSearchParam(value)
  //   setSelectedPage(1)
  // }

  const handleResetChartFilters = () => {
    setChartFrequency('')
    setChartType('')
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleDownload = () => {
    let queryParams = createQueryParams('table')

    props.actions.downloadRegisteredUsersData(queryParams).then((res) => {
      const date = new Date().toISOString().split('T')
      const dateNow = date[0].split('-').reverse().join('_')
      const timeNow = date[1].split('.')[0].split(':').join('_')

      downloadFileUTF8({
        data: res.value,
        fileName: `Registered-users-report_${dateNow}_${timeNow}.csv`,
        fileType: 'text/csv',
      })
    })
  }
  const toggleShowActionsMenu = (id) => {
    setShowActionsMenu(showActionsMenu === id ? null : id)
  }

  const getUniqueRegions = (regions) => {
    if (!regions || regions.length === 0) {
      return []
    }

    const names = regions.map((region) => region.name)
    const uniqueNames = [...new Set(names)]
    return uniqueNames.sort((a, b) => a.localeCompare(b))
  }
  const getTableData = (tableType, fullData) => {
    const data = fullData.filter((record) => record.year && record.month)
    if (tableType === 'month') {
      return data
    }
    if (tableType === 'year') {
      return Object.values(
        data?.reduce((acc, item) => {
          const key = `${item.year}-${item.accountType}`
          if (!acc[key]) {
            acc[key] = {
              year: item.year,
              accountType: item.accountType,
              total: 0,
              countries: [],
            }
          }
          acc[key].total += item.totalRegistered
          acc[key].countries = item.countries.reduce((countryAcc, country) => {
            if (!countryAcc.find((e) => e.countryCode === country.countryCode)) {
              countryAcc.push({
                countryCode: country.countryCode,
                count: 0,
              })
            }
            countryAcc.find((c) => c.countryCode === country.countryCode).count += country.count
            return countryAcc
          }, acc[key].countries)
          return acc
        }, {}),
      ).map((item) => ({
        year: item.year,
        accountType: item.accountType,
        totalRegistered: item.total,
        countries: item.countries,
      }))
    }
  }

  const getOrderedYearTableData = (data) => {
    if (!totalColOrder) return data
    const copy = [...data]
    copy.sort((a, b) => (totalColOrder === 'asc' ? a.total - b.total : b.total - a.total))
    return copy
  }

  const getChartData = (chartFrequency) => {
    const chartData = registeredUsersChartData.reduce((acc, record) => {
      let dateKey = ''
      if (chartFrequency === 'year') {
        dateKey = record.year?.toString()
      } else if (chartFrequency === 'quarter') {
        dateKey = `${record.year}-Q${record.quarter}`
      } else if (chartFrequency === 'month') {
        dateKey = `${record.year}-${record.month < 10 ? '0' + record.month : record.month}`
      } else {
        dateKey = record.year?.toString()
      }
      if (!acc[dateKey]) {
        acc[dateKey] = { date: dateKey }
      }
      acc[dateKey][record.accountType] = (acc[dateKey][record.accountType] || 0) + record.total
      return acc
    }, {})
    return Object.values(chartData).sort((a, b) => new Date(a.date) - new Date(b.date))
  }

  const renderChartSection = () => {
    return (
      <div className={`chart-section ${chartType === 'pie' ? 'pie-chart' : ''}`}>
        <div className="chart-filters-row">
          <div className="chart-title">{t('reports.registered_users_chart')}</div>
          <SimpleButton className="filter-button" onClick={() => setShowChartFiltersModal(true)}>
            <div className="icon-wrapper">
              <FilterTableIcon />
            </div>
            {t('reports.chart_styles')}
          </SimpleButton>
        </div>
        <UserReportChart data={getChartData(chartFrequency) || []} chartType={chartType} />
        {showChartFiltersModal && (
          <div className="chart-filters-modal-container">
            <FiltersModal
              filters={chartFilterOptions}
              name="styles"
              closeFilters={() => setShowChartFiltersModal(false)}
              modalClassName="chart-filters-modal"
              resetFilters={handleResetChartFilters}
              requireAllRequiredFilters
            />
          </div>
        )}
      </div>
    )
  }

  const renderUsersTable = () => {
    if (props.isTableLoading) {
      return (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )
    }

    if (!props.isTableLoading && !isFirstLoad && registeredUsersTableData.length === 0) {
      return <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
    }

    if (!isMobile) {
      return (
        <>
          <GenericTable
            data={registeredUsersTableShownData}
            headers={
              tableType === 'month'
                ? [
                    { title: t('year'), orderKey: 'year' },
                    { title: t('month') },
                    { title: t('account_context.account_type'), orderKey: 'accountType' },
                    { title: t('coltene_store.cart.total'), orderKey: 'total' },
                    { title: '' },
                  ]
                : [
                    { title: t('year'), orderKey: 'year' },
                    { title: t('account_context.account_type'), orderKey: 'accountType' },
                    { title: t('coltene_store.cart.total'), orderKey: 'total' },
                    { title: '' },
                  ]
            }
            keys={tableType === 'month' ? ['year', 'month', 'accountType', 'total', 'action'] : ['year', 'accountType', 'total', 'action']}
            keyRenderer={{
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item.id)
                    }}
                  >
                    {showActionsMenu === item.id ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item.id && (
                      <ActionsMenu
                        setHideMenu={() => setShowActionsMenu(null)}
                        actions={item.actions.map((a) => ({
                          ...a,
                          onActionClick: (e) => {
                            e?.stopPropagation?.()
                            setShowActionsMenu(null)
                            a.onActionClick(e)
                          },
                        }))}
                        isLoading={props.isLoading}
                      />
                    )}
                  </div>
                )
              },
            }}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            onRowClick={(row) => handleOpenViewModal(row)}
            isLoading={props.isTableLoading}
          />
          {visibleCount < registeredUsersTableData.length && (
            <div className="buttons-wrapper">
              <SimpleButton
                className="action-button"
                onClick={() => setVisibleCount((v) => Math.min(v + PAGE_SIZE, registeredUsersTableData.length))}
                disabled={props.isStatisticsDataLoading}
              >
                {t('load_more', { defaultValue: 'Load more' })}
              </SimpleButton>
            </div>
          )}
        </>
      )
    }

    if (isMobile) {
      return (
        <>
          {registeredUsersTableData.length > 0 && (
            <div className="order-by-mobile-wrapper">
              <Dropdown
                className="dropdown-input"
                placeholder={t('select_ordering')}
                options={orderingFields.map((field) => ({
                  key: field.translationKey,
                  value: t(`sortable_context.${field.translationKey}`),
                }))}
                initialSelectedKey={orderBy?.translationKey}
                onChange={(orderBy) => {
                  const sortOption = orderingFields.find((f) => f.translationKey === orderBy.key)
                  handleSort(sortOption)
                }}
              />
            </div>
          )}

          {registeredUsersTableShownData.map((record, index) => (
            <div key={index} className="card" onClick={() => setExpandedUser(record.id)}>
              <div className={'card-item' + (expandedUser !== record.id ? ' align-center' : '')}>
                <div className="card-item-title">{t('year')}</div>
                <div className={'card-item-body' + (expandedUser !== record.id ? ' align-center' : '')}>
                  <div>{record.year}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(record.id)
                    }}
                  >
                    {showActionsMenu === record.id ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === record.id && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={record.actions} isLoading={props.isTableLoading} />
                    )}
                  </div>
                </div>
              </div>
              {expandedUser === record.id && (
                <>
                  {tableType === 'month' && (
                    <div className="card-item">
                      <div className="card-item-title">{t('month')}</div>
                      <div className="card-item-body">{record.month}</div>
                    </div>
                  )}
                  <div className="card-item">
                    <div className="card-item-title">{t('account_context.account_type')}</div>
                    <div className="card-item-body">{record.accountType}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('coltene_store.cart.total')}</div>
                    <div className="card-item-body">{record.total}</div>
                  </div>
                </>
              )}
            </div>
          ))}
          {visibleCount < registeredUsersTableData.length && (
            <div className="buttons-wrapper">
              <SimpleButton
                className="action-button"
                onClick={() => setVisibleCount((v) => Math.min(v + PAGE_SIZE, registeredUsersTableData.length))}
                disabled={props.isStatisticsDataLoading}
              >
                {t('load_more', { defaultValue: 'Load more' })}
              </SimpleButton>
            </div>
          )}
        </>
      )
    }
  }
  const renderTableSection = () => (
    <div className="table-section">
      <div className="actions-wrapper">
        {/* <SearchWithFilter onSearch={handleSearch} /> */}
        <div className="links-month-year-wrapper">
          <span>{t('view')}:&nbsp;&nbsp;</span>
          <span
            className={`links-month-year ${tableType === 'month' ? 'links-month-year-font-color' : ''}`}
            onClick={() => {
              setTableType('month')
              setVisibleCount(PAGE_SIZE)
            }}
          >
            {t('month')}&nbsp;
          </span>
          <span>{'|'}&nbsp;&nbsp;</span>
          <span
            className={`links-month-year ${tableType === 'year' ? 'links-month-year-font-color' : ''}`}
            onClick={() => {
              setTableType('year')
              setVisibleCount(PAGE_SIZE)
            }}
          >
            {t('year')}
          </span>
        </div>
        <div className="table-actions-row">
          <SimpleButton className="action-button" onClick={() => handleDownload()} disabled={props.isTableLoading || !hasTable}>
            <DownloadIcon />
            {t('download')}
          </SimpleButton>
        </div>
      </div>
      {renderUsersTable()}
      {canLoadMore && (
        <div className="buttons-wrapper">
          <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
            {t('load_more')}
          </SimpleButton>
        </div>
      )}
    </div>
  )

  return (
    <div className="internal-reports-section registered-users">
      <div className="actions-wrapper page-section">
        <SearchWithFilter
          showFiltersModal={() => setShowFiltersModal(true)}
          onResetFilters={() => handleResetFilters()}
          showCounterBadge={(startDateFilter && endDateFilter) || selectedFilterCountry.length > 0 || selectedFilterRegion.length > 0}
          counterBadge={(startDateFilter && endDateFilter ? 1 : 0) + selectedFilterCountry.length + selectedFilterRegion.length}
        />
      </div>
      <div className="disclaimer-box-text">
        <div className="icon-wrapper">
          <InformationIcon />
        </div>
        <div className="disclaimer-wrapper">
          <div className="title">{t('reports.date_disclaimer')}</div>
        </div>
      </div>
      {registeredUsersChartData.length === 0 && registeredUsersTableData.length === 0 && !isFirstLoad ? (
        <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
      ) : (
        <>
          {renderChartSection()}
          {renderTableSection()}
        </>
      )}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('date'),
              type: FilterType.dateRange,
              dataset: null,
              input: { startDate: startDateFilter, endDate: endDateFilter },
              output: (startDate, endDate) => {
                setStartDateFilter(startDate)
                setEndDateFilter(endDate)
              },
              quickLinks: [
                { label: t('1_month'), months: 1 },
                { label: t('3_months'), months: 3 },
                { label: t('6_months'), months: 6 },
                { label: t('12_months'), months: 12 },
              ],
            },
            {
              name: t('country'),
              type: FilterType.multiSelect,
              dataset: props.registeredUsersCountries.map((c) => c.name),
              input: selectedFilterCountry,
              output: (country) => setSelectedFilterCountry(country),
            },
            {
              name: t('region'),
              type: FilterType.multiSelect,
              dataset: getUniqueRegions(props.reportsRegions),
              input: selectedFilterRegion,
              output: (region) => setSelectedFilterRegion(region),
            },
          ]}
          shouldFilter={(shouldApply) => {
            setSelectedPage(1)
            setShouldApplyFilters(shouldApply)
          }}
          resetFilters={handleResetFilters}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {props.isTableLoading && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
      {showViewModal && <ViewUserModal registeredUsersTableData={registeredUsersTableData} rowId={rowId} closeModal={closeModal} tableType={tableType} />}
    </div>
  )
}

function stateToProps({ reports, shared }) {
  return {
    registeredUsersChartData: reports?.registeredUsersChartData || [],
    registeredUsersTableData: reports?.registeredUsersTableData || [],
    registeredUsersCountries: reports?.registeredUsersCountries || [],
    reportsRegions: shared?.regions || [],
    isChartLoading: reports?.isChartLoading,
    isTableLoading: reports?.isTableLoading,
    canLoadMore: reports?.canLoadMoreTableData,
    errorMessage: reports?.errorMessage,
    successMessage: reports?.successMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadRegisteredUsersChartData,
        loadRegisteredUsersTableData,
        downloadRegisteredUsersData,
        loadRegisteredUsersCountries,
        loadRegions,
        clearReportsMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(RegisteredUsers)

